import type { FormEvent, KeyboardEvent, RefObject } from 'react';
import { SendHorizontal, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { MAX_MESSAGES } from './constants';

type EmoviDocChatFormProps = {
  inputValue: string;
  setInputValue: (value: string) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onKeyDown: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
  isStreaming: boolean;
  inputRef: RefObject<HTMLTextAreaElement | null>;
  chatError: string | null;
  setChatError: (value: string | null) => void;
  shouldShowWarning: boolean;
  userMessageCount: number;
  hasReachedLimit: boolean;
  onCreateConversation: () => void;
  isCreatingConversation: boolean;
};

export function EmoviDocChatForm({
  inputValue,
  setInputValue,
  onSubmit,
  onKeyDown,
  isStreaming,
  inputRef,
  chatError,
  setChatError,
  shouldShowWarning,
  userMessageCount,
  hasReachedLimit,
  onCreateConversation,
  isCreatingConversation,
}: EmoviDocChatFormProps) {
  return (
    <div className="flex-shrink-0 border-t border-border p-3 space-y-2">
      {chatError ? (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive flex items-center gap-2">
          <TriangleAlert className="h-3.5 w-3.5" />
          {chatError}
        </div>
      ) : null}

      {shouldShowWarning ? (
        <div className="rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning-foreground flex items-center gap-2">
          <TriangleAlert className="h-3.5 w-3.5" />
          Stai per raggiungere il limite di questa conversazione ({userMessageCount}/{MAX_MESSAGES}). Valuta di
          aprirne una nuova.
        </div>
      ) : null}

      {hasReachedLimit ? (
        <div className="rounded-lg border border-border bg-muted/30 px-4 py-3 text-center space-y-2">
          <p className="text-sm text-foreground">
            Hai raggiunto il limite di messaggi per questa conversazione.
          </p>
          <Button
            type="button"
            size="sm"
            onClick={onCreateConversation}
            isLoading={isCreatingConversation}
          >
            Nuova conversazione
          </Button>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-2">
          <div className="rounded-2xl border border-primary/50 bg-primary/10 px-2 py-1.5">
            <Textarea
              ref={inputRef}
              value={inputValue}
              placeholder="Scrivi un messaggio..."
              className="resize-none border-0 bg-transparent p-1.5 min-h-[52px] sm:min-h-[72px]"
              onChange={(event) => {
                setInputValue(event.target.value);
                if (chatError) setChatError(null);
              }}
              onKeyDown={onKeyDown}
              disabled={isStreaming}
            />
          </div>
          <div className="flex justify-end">
            <Button
              type="submit"
              size="sm"
              variant="default"
              isLoading={isStreaming}
              disabled={!inputValue.trim()}
            >
              <SendHorizontal className="h-4 w-4" />
              Invia
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
